import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, KeyRound, Shield, Trash2, Search, Power, Link2, Copy, Mail, X, ChevronDown } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Input, Select, Pill } from "../../components/common/Primitives";
import { Modal, ModalFooter } from "../../components/common/Modal";
import { useAuth } from "../../lib/AuthContext";
import api, { formatApiError } from "../../lib/api";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  agent: "Agent",
  startup: "Startup",
  investor: "Investor",
  debt_head: "Debt Head",
  debt_manager: "Debt Manager",
  debt_agent: "Debt Agent",
  credit_analyst: "Credit Analyst",
  channel_partner: "Channel Partner",
};

const roleToneOf = (r) => {
  if (r === "super_admin" || r === "admin") return "gold";
  return "muted";
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", role: "", status: "" });
  const [catalog, setCatalog] = useState(null);
  // Bulk selection — a Set of user_id strings currently checked.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(null); // {action, role?}

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [permsUser, setPermsUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.role) params.role = filters.role;
    if (filters.status) params.status = filters.status;
    api.get("/admin/users", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/users/_meta/catalog")
      .then((r) => setCatalog(r.data))
      .catch(() => {});
  }, []);
  // Clear selection whenever the underlying row list changes so orphaned
  // IDs don't linger.
  useEffect(() => {
    setSelected((prev) => {
      const stillHere = new Set(rows.map((r) => r.user_id));
      const next = new Set([...prev].filter((id) => stillHere.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const manageableRoles = catalog?.manageable_roles_for_actor || [];
  const canCreate = manageableRoles.length > 0;

  const canManage = (u) => manageableRoles.includes(u.role) && u.user_id !== me?.user_id;
  const canDelete = (u) => me?.role === "super_admin" && u.user_id !== me?.user_id;
  const canEditPermissions = (u) => canManage(u) && !me?.demo_account && !u?.demo_account;

  // Only rows the actor can act on are selectable — prevents surprise
  // "0 succeeded, N failed" toasts when you check everything blindly.
  const selectableRows = useMemo(() => rows.filter(canManage), [rows, manageableRoles, me?.user_id]);
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.user_id)), [rows, selected]);
  const allSelectableSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.user_id));
  const someSelected = selected.size > 0;

  const toggleOne = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelectableSelected) return new Set();
      const next = new Set(prev);
      selectableRows.forEach((r) => next.add(r.user_id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, role) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    try {
      const payload = { user_ids: ids, action };
      if (role) payload.role = role;
      const { data } = await api.post("/admin/users/bulk", payload);
      const { succeeded, failed, results } = data;
      if (failed > 0) {
        const failReasons = (results || []).filter((x) => !x.ok).map((x) => x.error).slice(0, 3).join(", ");
        toast.warning(`${succeeded} ok, ${failed} failed. ${failReasons ? `First reasons: ${failReasons}` : ""}`);
      } else {
        toast.success(`${succeeded} user(s) ${action === "delete" ? "deleted" : action === "set_role" ? `set to ${role}` : action + "d"}.`);
      }
      clearSelection();
      setBulkOpen(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardShell title="Users & Access" subtitle="ADMIN · IAM">
      <Toaster richColors position="top-right" />

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-5">
        <div className="flex-1 min-w-[220px]">
          <Input
            label="Search"
            placeholder="Name or email…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            testId="users-search-input"
          />
        </div>
        <div className="w-full md:w-48">
          <Select
            label="Role"
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            testId="users-role-filter"
          >
            <option value="">All roles</option>
            {Object.entries(ROLE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
        </div>
        <div className="w-full md:w-40">
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            testId="users-status-filter"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
        <Button
          variant="gold"
          onClick={() => setCreateOpen(true)}
          disabled={!canCreate}
          data-testid="users-new-btn"
          className="whitespace-nowrap"
        >
          <span className="inline-flex items-center gap-1.5"><Plus size={16} /> New User</span>
        </Button>
      </div>

      {/* Bulk action bar — only appears when at least one row is selected.
          Copper accent so it visually pops without competing with primary
          navy CTAs elsewhere on the page. */}
      {someSelected && (
        <div
          className="mb-4 flex flex-wrap items-center gap-2 border border-gold/40 bg-gold/[0.08] backdrop-blur rounded-md px-4 py-3"
          data-testid="users-bulk-bar"
        >
          <div className="text-sm font-heading text-ink flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gold text-navy text-xs font-mono font-semibold">{selected.size}</span>
            <span>selected</span>
          </div>
          <div className="h-4 w-px bg-navy/15 mx-1" />
          <Button
            variant="ghost"
            className="text-xs !py-1.5"
            onClick={() => setBulkOpen({ action: "activate" })}
            data-testid="users-bulk-activate"
          >Activate</Button>
          <Button
            variant="ghost"
            className="text-xs !py-1.5"
            onClick={() => setBulkOpen({ action: "deactivate" })}
            data-testid="users-bulk-deactivate"
          >Deactivate</Button>
          {me?.role === "super_admin" && (
            <BulkSetRoleMenu
              manageableRoles={manageableRoles}
              onPick={(role) => setBulkOpen({ action: "set_role", role })}
            />
          )}
          {me?.role === "super_admin" && (
            <Button
              variant="ghost"
              className="text-xs !py-1.5 !text-rose-700 hover:!bg-rose-50"
              onClick={() => setBulkOpen({ action: "delete" })}
              data-testid="users-bulk-delete"
            >
              <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Delete</span>
            </Button>
          )}
          <button
            type="button"
            className="ml-auto text-[11px] font-mono uppercase tracking-widest text-ink-muted hover:text-ink inline-flex items-center gap-1"
            onClick={clearSelection}
            data-testid="users-bulk-clear"
          >
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-white/10 bg-white/70 backdrop-blur overflow-x-auto rounded-md">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
            <tr className="border-b border-navy/10">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={toggleAll}
                  disabled={selectableRows.length === 0}
                  aria-label="Select all manageable users"
                  data-testid="users-select-all"
                  className="h-4 w-4 cursor-pointer accent-[#8A6600] disabled:opacity-40"
                />
              </th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Auth</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-ink-muted">No users match the filters.</td></tr>
            ) : rows.map((r) => {
              const rowSelectable = canManage(r);
              const isChecked = selected.has(r.user_id);
              return (
              <tr
                key={r.user_id}
                className={`border-b border-navy/5 hover:bg-navy/[0.02] ${isChecked ? "bg-gold/[0.06]" : ""}`}
                data-testid={`user-row-${r.user_id}`}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={!rowSelectable}
                    onChange={() => toggleOne(r.user_id)}
                    aria-label={`Select ${r.email}`}
                    data-testid={`user-select-${r.user_id}`}
                    className="h-4 w-4 cursor-pointer accent-[#8A6600] disabled:opacity-30"
                    title={rowSelectable ? "Select for bulk action" : "You cannot manage this user"}
                  />
                </td>
                <td className="px-4 py-3 text-ink font-medium">{r.name}</td>
                <td className="px-4 py-3 text-ink-soft">{r.email}</td>
                <td className="px-4 py-3"><Pill tone={roleToneOf(r.role)}>{ROLE_LABEL[r.role] || r.role}</Pill></td>
                <td className="px-4 py-3">
                  {r.is_active === false
                    ? <Pill tone="muted">Inactive</Pill>
                    : <Pill tone="gold">Active</Pill>}
                </td>
                <td className="px-4 py-3 text-ink-muted font-mono text-xs">{r.auth_provider || "password"}</td>
                <td className="px-4 py-3 text-ink-muted font-mono text-xs">{r.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn
                      title="Edit"
                      testId={`user-edit-${r.user_id}`}
                      onClick={() => setEditUser(r)}
                      disabled={!canManage(r)}
                    ><Pencil size={14} /></IconBtn>
                    <IconBtn
                      title={
                        me?.demo_account
                          ? "Permissions (disabled in demo mode)"
                          : r?.demo_account
                            ? "Permissions (this user is a demo account)"
                            : "Permissions"
                      }
                      testId={`user-perms-${r.user_id}`}
                      onClick={() => setPermsUser(r)}
                      disabled={!canEditPermissions(r)}
                    ><Shield size={14} /></IconBtn>
                    <IconBtn
                      title="Reset password"
                      testId={`user-reset-${r.user_id}`}
                      onClick={() => setResetUser(r)}
                      disabled={!canManage(r)}
                    ><KeyRound size={14} /></IconBtn>
                    <IconBtn
                      title={r.is_active === false ? "Activate" : "Deactivate"}
                      testId={`user-toggle-${r.user_id}`}
                      onClick={() => toggleStatus(r, load)}
                      disabled={!canManage(r)}
                    ><Power size={14} /></IconBtn>
                    <IconBtn
                      title="Delete"
                      testId={`user-delete-${r.user_id}`}
                      onClick={() => setDeleteUser(r)}
                      disabled={!canDelete(r)}
                      danger
                    ><Trash2 size={14} /></IconBtn>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {createOpen && (
        <UserFormModal
          mode="create"
          catalog={catalog}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); load(); }}
        />
      )}
      {editUser && (
        <UserFormModal
          mode="edit"
          user={editUser}
          catalog={catalog}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
      {permsUser && (
        <PermissionsModal
          user={permsUser}
          catalog={catalog}
          onClose={() => setPermsUser(null)}
          onSaved={() => { setPermsUser(null); load(); }}
        />
      )}
      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
          onSaved={() => setResetUser(null)}
        />
      )}
      {deleteUser && (
        <DeleteConfirmModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onDeleted={() => { setDeleteUser(null); load(); }}
        />
      )}
      {bulkOpen && (
        <BulkConfirmModal
          action={bulkOpen.action}
          role={bulkOpen.role}
          users={selectedRows}
          onClose={() => setBulkOpen(null)}
          onConfirm={() => runBulk(bulkOpen.action, bulkOpen.role)}
        />
      )}
    </DashboardShell>
  );
}

// ------------------------------------------------------------------
// Bulk set-role dropdown — mini menu triggered from the bulk bar.
// ------------------------------------------------------------------
function BulkSetRoleMenu({ manageableRoles, onPick }) {
  const [open, setOpen] = useState(false);
  const options = (manageableRoles || []).filter((r) => r !== "super_admin");
  if (options.length === 0) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs !py-1.5 px-3 rounded-md border border-navy/20 bg-white/70 hover:border-gold/50 font-heading inline-flex items-center gap-1"
        data-testid="users-bulk-setrole-trigger"
      >
        Set role <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-30 min-w-[180px] bg-white border border-navy/15 rounded-md shadow-[0_12px_30px_rgba(15,61,46,0.15)] py-1"
          data-testid="users-bulk-setrole-menu"
        >
          {options.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => { setOpen(false); onPick(role); }}
              className="w-full text-left px-3 py-1.5 text-sm text-ink hover:bg-navy/[0.04]"
              data-testid={`users-bulk-setrole-${role}`}
            >
              {ROLE_LABEL[role] || role}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Bulk confirm modal — dry-lists targets before firing off the request.
// ------------------------------------------------------------------
function BulkConfirmModal({ action, role, users, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const verb = action === "delete" ? "Delete"
    : action === "activate" ? "Activate"
    : action === "deactivate" ? "Deactivate"
    : action === "set_role" ? `Set role → ${ROLE_LABEL[role] || role}`
    : action;
  const danger = action === "delete";
  const doIt = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <Modal open title={`${verb} · ${users.length} user(s)`} onClose={onClose} testId="users-bulk-confirm">
      <div className="space-y-3">
        <p className="text-sm text-ink-soft">
          {danger
            ? "This will permanently remove the users below. Any linked profile data (startup / investor / debt case) may become orphaned — proceed only after export."
            : action === "set_role"
              ? `Every user below will be set to role "${ROLE_LABEL[role] || role}". Their KYC and profile data stay intact.`
              : `Every user below will be ${action}d.`}
        </p>
        <div className="max-h-56 overflow-y-auto border border-navy/10 rounded bg-white/60">
          <ul className="divide-y divide-navy/5">
            {users.map((u) => (
              <li key={u.user_id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`users-bulk-preview-${u.user_id}`}>
                <span className="font-heading text-ink truncate flex-1">{u.name}</span>
                <span className="text-ink-muted font-mono text-xs truncate">{u.email}</span>
                <Pill tone={roleToneOf(u.role)}>{ROLE_LABEL[u.role] || u.role}</Pill>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} data-testid="users-bulk-confirm-cancel">Cancel</Button>
        <Button
          onClick={doIt}
          disabled={busy}
          variant={danger ? "danger" : "gold"}
          data-testid="users-bulk-confirm-apply"
        >{busy ? "Working…" : verb}</Button>
      </ModalFooter>
    </Modal>
  );
}

// ------------------------------------------------------------------
// Icon button
// ------------------------------------------------------------------
function IconBtn({ children, title, onClick, disabled, danger, testId }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`p-1.5 rounded transition-colors ${
        disabled
          ? "text-ink-faint cursor-not-allowed"
          : danger
          ? "text-rose-600 hover:bg-rose-50"
          : "text-ink-soft hover:bg-navy/5 hover:text-ink"
      }`}
    >{children}</button>
  );
}

// ------------------------------------------------------------------
// Toggle status
// ------------------------------------------------------------------
async function toggleStatus(user, reload) {
  const next = !(user.is_active !== false);
  try {
    await api.patch(`/admin/users/${user.user_id}/status`, { is_active: next });
    toast.success(next ? "User activated" : "User deactivated");
    reload();
  } catch (e) { toast.error(formatApiError(e)); }
}

// ------------------------------------------------------------------
// Create / Edit modal
// ------------------------------------------------------------------
function UserFormModal({ mode, user, catalog, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    role: user?.role || (catalog?.manageable_roles_for_actor?.[0] || "agent"),
    mobile: user?.mobile || "",
    approval_status: user?.approval_status || "Approved",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/users/${user.user_id}`, {
          name: form.name,
          role: form.role,
          mobile: form.mobile,
          approval_status: form.approval_status,
        });
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const availableRoles = catalog?.manageable_roles_for_actor || [];

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit user" : "Create new user"}
      subtitle={isEdit ? user?.email : "The new user will receive access immediately"}
      testId="user-form-modal"
      size="md"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="user-form">
        <Input
          label="Full name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          testId="user-form-name"
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          disabled={isEdit}
          testId="user-form-email"
        />
        {!isEdit && (
          <Input
            label="Password (min 6)"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={6}
            testId="user-form-password"
          />
        )}
        <Select
          label="Role"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          testId="user-form-role"
        >
          {availableRoles.map((r) => (
            <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
          ))}
        </Select>
        <Input
          label="Mobile (optional)"
          value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          testId="user-form-mobile"
        />
        {isEdit && (
          <Select
            label="Approval status"
            value={form.approval_status}
            onChange={(e) => setForm({ ...form, approval_status: e.target.value })}
            testId="user-form-approval"
          >
            <option value="Draft">Draft</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </Select>
        )}
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="user-form-cancel">Cancel</Button>
          <Button type="submit" variant="gold" disabled={saving} data-testid="user-form-submit">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------
// Permissions modal
// ------------------------------------------------------------------
function PermissionsModal({ user, catalog, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allow, setAllow] = useState(new Set());
  const [deny, setDeny] = useState(new Set());

  useEffect(() => {
    api.get(`/admin/users/${user.user_id}`).then((r) => {
      setDetail(r.data);
      setAllow(new Set(r.data?.permission_overrides?.allow || []));
      setDeny(new Set(r.data?.permission_overrides?.deny || []));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [user.user_id]);

  const roleDefaults = useMemo(() => new Set(detail?.role_defaults || []), [detail]);

  const grouped = useMemo(() => {
    const g = {};
    (catalog?.permissions || []).forEach((p) => {
      (g[p.category] ||= []).push(p);
    });
    return g;
  }, [catalog]);

  const isChecked = (key) => {
    if (deny.has(key)) return false;
    if (allow.has(key)) return true;
    return roleDefaults.has(key);
  };

  const toggle = (key) => {
    const inDefault = roleDefaults.has(key);
    const currentlyOn = isChecked(key);
    const newAllow = new Set(allow);
    const newDeny = new Set(deny);
    // Turn OFF
    if (currentlyOn) {
      newAllow.delete(key);
      if (inDefault) newDeny.add(key);
    }
    // Turn ON
    else {
      newDeny.delete(key);
      if (!inDefault) newAllow.add(key);
    }
    setAllow(newAllow);
    setDeny(newDeny);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.user_id}/permissions`, {
        allow: Array.from(allow),
        deny: Array.from(deny),
      });
      toast.success("Permissions updated");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const isSuperAdmin = user.role === "super_admin";

  return (
    <Modal
      open
      onClose={onClose}
      title={`Permissions — ${user.name}`}
      subtitle={`${ROLE_LABEL[user.role]} · ${user.email}`}
      testId="user-permissions-modal"
      size="lg"
    >
      {isSuperAdmin && (
        <div className="mb-4 border border-gold/40 bg-gold/10 text-ink text-xs px-3 py-2 rounded">
          Super Admin has all permissions by design. Overrides are ignored.
        </div>
      )}
      <div className="text-xs text-ink-muted mb-4">
        Turning a permission <em>on</em> that is not in the role default adds it to the user&apos;s <b>allow</b> list.
        Turning <em>off</em> a permission that is in the role default adds it to the <b>deny</b> list.
      </div>
      {!detail ? (
        <div className="text-ink-muted text-sm">Loading…</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, perms]) => (
            <div key={category}>
              <div className="overline mb-2">{category}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {perms.map((p) => {
                  const checked = isChecked(p.key);
                  const inDefault = roleDefaults.has(p.key);
                  const overrideAllow = allow.has(p.key);
                  const overrideDeny = deny.has(p.key);
                  return (
                    <label
                      key={p.key}
                      className="flex items-start gap-3 p-2.5 border border-navy/10 rounded cursor-pointer hover:bg-navy/[0.02]"
                      data-testid={`perm-row-${p.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(p.key)}
                        disabled={isSuperAdmin}
                        className="mt-0.5 accent-navy"
                        data-testid={`perm-check-${p.key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink font-medium">{p.label}</div>
                        <div className="text-xs text-ink-muted font-mono truncate">{p.key}</div>
                        {(overrideAllow || overrideDeny) && (
                          <div className="mt-1">
                            {overrideAllow && <Pill tone="gold">Override: allow</Pill>}
                            {overrideDeny && <Pill tone="muted">Override: deny</Pill>}
                          </div>
                        )}
                        {!overrideAllow && !overrideDeny && inDefault && (
                          <div className="mt-1"><span className="text-[10px] text-ink-faint">Role default</span></div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} data-testid="perms-cancel">Cancel</Button>
        <Button
          variant="gold"
          onClick={submit}
          disabled={saving || isSuperAdmin}
          data-testid="perms-save"
        >
          {saving ? "Saving…" : "Save permissions"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ------------------------------------------------------------------
// Reset password modal — direct password + shareable reset link + email log
// ------------------------------------------------------------------
function ResetPasswordModal({ user, onClose, onSaved }) {
  const [tab, setTab] = useState("direct"); // 'direct' | 'link'
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${user.user_id}/email-events?limit=5`)
      .then((r) => setEvents(r.data?.items || []))
      .catch(() => setEvents([]));
  }, [user.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.user_id}/reset-password`, { new_password: pwd });
      toast.success("Password reset. Share the temporary password securely.");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const generateLink = async () => {
    setGenLoading(true);
    try {
      const r = await api.post(`/admin/users/${user.user_id}/generate-reset-link`);
      setLink(r.data);
      if (r.data?.email_sent) {
        toast.success("Reset link generated and emailed to the user.");
      } else {
        toast.info("Reset link generated. Email delivery failed — copy the link below and share it manually.");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setGenLoading(false); }
  };

  const copyLink = async () => {
    if (!link?.reset_url) return;
    try {
      await navigator.clipboard.writeText(link.reset_url);
      toast.success("Reset link copied to clipboard");
    } catch { toast.error("Could not copy — please select and copy manually."); }
  };

  const eventTone = (s) => (s === "sent" ? "gold" : s === "skipped_unsubscribed" ? "muted" : "muted");

  return (
    <Modal open onClose={onClose} title="Reset password" subtitle={user.email} testId="reset-pwd-modal" size="md">
      {/* Tab switcher */}
      <div className="flex items-center gap-1 border-b border-navy/10 mb-4 -mt-1">
        <button
          type="button"
          onClick={() => setTab("direct")}
          className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
            tab === "direct" ? "border-navy text-ink" : "border-transparent text-ink-muted hover:text-ink"
          }`}
          data-testid="reset-tab-direct"
        >Set new password</button>
        <button
          type="button"
          onClick={() => setTab("link")}
          className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
            tab === "link" ? "border-navy text-ink" : "border-transparent text-ink-muted hover:text-ink"
          }`}
          data-testid="reset-tab-link"
        >Generate reset link</button>
      </div>

      {tab === "direct" ? (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-pwd-form">
          <Input
            label="New password (min 6)"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            required
            minLength={6}
            testId="reset-pwd-input"
          />
          <div className="text-xs text-ink-muted">
            The user will be able to sign in with this password immediately. Share it securely.
          </div>
          <ModalFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="gold" type="submit" disabled={saving} data-testid="reset-pwd-submit">
              {saving ? "Resetting…" : "Reset password"}
            </Button>
          </ModalFooter>
        </form>
      ) : (
        <div className="space-y-4" data-testid="reset-link-panel">
          <div className="text-xs text-ink-muted">
            Generates a 30-minute reset link and (best effort) emails it. If Resend is in test mode or
            the recipient domain isn&apos;t verified, the email may bounce &mdash; copy the link and share it directly.
          </div>
          {!link ? (
            <Button
              variant="gold"
              onClick={generateLink}
              disabled={genLoading}
              data-testid="reset-link-generate-btn"
            >
              <span className="inline-flex items-center gap-1.5">
                <Link2 size={14} /> {genLoading ? "Generating…" : "Generate reset link"}
              </span>
            </Button>
          ) : (
            <div className="space-y-3">
              <div className={`border p-3 rounded text-xs ${
                link.email_sent
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-amber-200 bg-amber-50 text-amber-900"
              }`}>
                <div className="font-semibold mb-1">
                  {link.email_sent ? "Email sent via Resend ✓" : "Email delivery failed — share the link manually"}
                </div>
                <div className="opacity-80 break-all">{link.email_reason || "Delivered"}</div>
              </div>
              <label className="block">
                <div className="overline mb-1">Reset URL (expires in 30 min)</div>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={link.reset_url}
                    className="flex-1 px-3 py-2 border border-navy/15 rounded font-mono text-[11px] bg-white/70"
                    data-testid="reset-link-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button variant="ghost" type="button" onClick={copyLink} data-testid="reset-link-copy-btn">
                    <span className="inline-flex items-center gap-1.5"><Copy size={14} /> Copy</span>
                  </Button>
                </div>
              </label>
              <div className="text-xs text-ink-muted">
                Expires <span className="font-mono">{new Date(link.expires_at).toLocaleString()}</span>
              </div>
            </div>
          )}
          {/* Recent email events */}
          <div className="pt-3 border-t border-navy/10">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={13} className="text-ink-muted" />
              <div className="overline">Recent emails to {user.email}</div>
            </div>
            {events === null ? (
              <div className="text-xs text-ink-muted">Loading…</div>
            ) : events.length === 0 ? (
              <div className="text-xs text-ink-muted">No email attempts recorded yet.</div>
            ) : (
              <div className="space-y-1.5">
                {events.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px]" data-testid={`email-event-${i}`}>
                    <Pill tone={eventTone(e.status)}>{e.status}</Pill>
                    <div className="flex-1 min-w-0">
                      <div className="text-ink truncate">{e.subject}</div>
                      <div className="text-ink-muted font-mono truncate">
                        {(e.template || "—")} · {e.created_at?.slice(0, 19).replace("T", " ")}
                        {e.reason ? ` · ${String(e.reason).slice(0, 80)}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <ModalFooter>
            <Button variant="ghost" type="button" onClick={onClose}>Close</Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------
// Delete confirmation
// ------------------------------------------------------------------
function DeleteConfirmModal({ user, onClose, onDeleted }) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canConfirm = confirmEmail.trim().toLowerCase() === user.email.toLowerCase();

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${user.user_id}`);
      toast.success("User deleted");
      onDeleted?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };
  return (
    <Modal open onClose={onClose} title="Delete user" subtitle={user.email} testId="delete-user-modal" size="sm">
      <div className="space-y-4">
        <div className="border border-rose-200 bg-rose-50 text-rose-900 text-sm p-3 rounded">
          This will permanently delete the account. This cannot be undone. Any startups or investors owned by this user will be orphaned.
        </div>
        <Input
          label={`Type "${user.email}" to confirm`}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          testId="delete-user-confirm-input"
        />
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            onClick={doDelete}
            disabled={!canConfirm || deleting}
            data-testid="delete-user-confirm-btn"
          >
            {deleting ? "Deleting…" : "Delete permanently"}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
